import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import type { TicketFolder } from '../../types/tickets';
import {
  isTicketFooterFolder,
  splitTicketFolderTree,
  type TicketFolderNode,
} from '../../lib/tickets';

type Props = {
  folders: TicketFolder[];
  unreadByFolder: Record<string, number>;
  activeFolderId: string | null;
  onSelect: (folderId: string) => void;
  onCreateFolder: (name: string, parentId?: string | null) => Promise<void>;
  onDeleteFolder: (folderId: string) => Promise<void>;
  onDropTicket?: (ticketId: string, folderId: string) => void;
  drawerOpen?: boolean;
  /** IT Management starts without custom folders — hide create controls. */
  allowCreateFolders?: boolean;
};

function folderUnreadTotal(
  node: TicketFolderNode,
  unreadByFolder: Record<string, number>,
): number {
  let total = unreadByFolder[node.id] ?? 0;
  for (const child of node.children) {
    total += folderUnreadTotal(child, unreadByFolder);
  }
  return total;
}

function SubfolderRow({
  folder,
  unread,
  active,
  dragOverId,
  setDragOverId,
  onSelect,
  onDropTicket,
  onDelete,
}: {
  folder: TicketFolderNode;
  unread: number;
  active: boolean;
  dragOverId: string | null;
  setDragOverId: (id: string | null) => void;
  onSelect: (folderId: string) => void;
  onDropTicket?: (ticketId: string, folderId: string) => void;
  onDelete: (folder: TicketFolder) => void;
}) {
  const dragOver = dragOverId === folder.id;
  return (
    <li>
      <div
        className={`tickets-sidebar__row tickets-sidebar__row--sub${
          active ? ' tickets-sidebar__row--active' : ''
        }${dragOver ? ' tickets-sidebar__item--drag-over' : ''}`}
      >
        <button
          type="button"
          className={`tickets-sidebar__item${active ? ' tickets-sidebar__item--active' : ''}`}
          onClick={() => onSelect(folder.id)}
          onDragOver={(e) => {
            if (!onDropTicket) return;
            e.preventDefault();
            setDragOverId(folder.id);
          }}
          onDragLeave={() => {
            if (dragOverId === folder.id) setDragOverId(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverId(null);
            const ticketId = e.dataTransfer.getData('text/ticket-id');
            if (ticketId && onDropTicket) onDropTicket(ticketId, folder.id);
          }}
        >
          <span className="tickets-sidebar__item-main">
            <span className="tickets-sidebar__name">{folder.name}</span>
          </span>
          {unread > 0 ? <span className="tickets-sidebar__badge">{unread}</span> : null}
        </button>
        {!folder.is_system ? (
          <span className="tickets-sidebar__item-actions">
            <button
              type="button"
              className="tickets-sidebar__mini tickets-sidebar__mini--danger"
              title="Delete folder"
              onClick={() => onDelete(folder)}
            >
              ×
            </button>
          </span>
        ) : null}
      </div>
    </li>
  );
}

function SubfolderPanel({
  node,
  unreadByFolder,
  activeFolderId,
  dragOverId,
  creatingParentId,
  newName,
  busy,
  onSelect,
  onDropTicket,
  onStartCreate,
  onDelete,
  setDragOverId,
  setNewName,
  submitFolder,
  allowCreateFolders = true,
}: {
  node: TicketFolderNode;
  unreadByFolder: Record<string, number>;
  activeFolderId: string | null;
  dragOverId: string | null;
  creatingParentId: string | 'root' | null;
  newName: string;
  busy: boolean;
  onSelect: (folderId: string) => void;
  onDropTicket?: (ticketId: string, folderId: string) => void;
  onStartCreate: (parentId: string) => void;
  onDelete: (folder: TicketFolder) => void;
  setDragOverId: (id: string | null) => void;
  setNewName: (value: string) => void;
  submitFolder: () => void;
  allowCreateFolders?: boolean;
}) {
  const allowSubfolder = allowCreateFolders && !isTicketFooterFolder(node);
  const creatingHere = creatingParentId === node.id;
  const creatingInboxRoot = creatingParentId === 'root' && node.slug === 'inbox';
  const hasChildren = node.children.length > 0;

  return (
    <div className="tickets-sidebar__dock-panel-inner">
      <div className="tickets-sidebar__dock-panel-head">
        <p className="tickets-sidebar__dock-panel-title">{node.name}</p>
        {allowSubfolder && node.slug === 'inbox' ? (
          <button
            type="button"
            className="tickets-sidebar__add"
            title="Add subfolder under Inbox"
            onClick={() => onStartCreate(node.id)}
          >
            +
          </button>
        ) : allowSubfolder && node.slug !== 'inbox' ? (
          <button
            type="button"
            className="tickets-sidebar__mini"
            title="Add subfolder"
            onClick={() => onStartCreate(node.id)}
          >
            +
          </button>
        ) : null}
      </div>

      {hasChildren ? (
        <ul className="tickets-sidebar__list tickets-sidebar__list--nested">
          {node.children.map((child) => (
            <SubfolderRow
              key={child.id}
              folder={child}
              unread={unreadByFolder[child.id] ?? 0}
              active={child.id === activeFolderId}
              dragOverId={dragOverId}
              setDragOverId={setDragOverId}
              onSelect={onSelect}
              onDropTicket={onDropTicket}
              onDelete={onDelete}
            />
          ))}
        </ul>
      ) : (
        <p className="tickets-sidebar__dock-panel-empty">No subfolders</p>
      )}

      {creatingHere || creatingInboxRoot ? (
        <div className="tickets-sidebar__create tickets-sidebar__create--dock">
          <input
            className="field-input field-input--compact"
            placeholder="Subfolder name"
            value={newName}
            autoFocus
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitFolder();
            }}
          />
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy}
            onClick={() => void submitFolder()}
          >
            Add
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function TicketFolderSidebar({
  folders,
  unreadByFolder,
  activeFolderId,
  onSelect,
  onCreateFolder,
  onDeleteFolder,
  onDropTicket,
  drawerOpen = false,
  allowCreateFolders = true,
}: Props) {
  const [creatingParentId, setCreatingParentId] = useState<string | 'root' | null>(null);
  const [newName, setNewName] = useState('');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [ticketDragActive, setTicketDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [peekSectionId, setPeekSectionId] = useState<string | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const creatingParentIdRef = useRef(creatingParentId);
  const ticketDragActiveRef = useRef(ticketDragActive);
  creatingParentIdRef.current = creatingParentId;
  ticketDragActiveRef.current = ticketDragActive;

  const { main, footer } = useMemo(() => splitTicketFolderTree(folders), [folders]);

  const sectionByActiveFolder = useMemo(() => {
    for (const node of main) {
      if (node.id === activeFolderId) return node;
      if (node.children.some((child) => child.id === activeFolderId)) return node;
    }
    return null;
  }, [main, activeFolderId]);

  const panelSection = useMemo(() => {
    if (drawerOpen) {
      if (peekSectionId) {
        return main.find((node) => node.id === peekSectionId) ?? null;
      }
      return sectionByActiveFolder;
    }
    if (!peekSectionId) return null;
    return main.find((node) => node.id === peekSectionId) ?? null;
  }, [drawerOpen, peekSectionId, sectionByActiveFolder, main]);

  useEffect(() => {
    if (!drawerOpen || peekSectionId) return;
    if (!sectionByActiveFolder || isTicketFooterFolder(sectionByActiveFolder)) return;
    if (sectionByActiveFolder.children.length > 0 || sectionByActiveFolder.slug === 'inbox') {
      setPeekSectionId(sectionByActiveFolder.id);
    }
  }, [drawerOpen, peekSectionId, sectionByActiveFolder]);

  useEffect(() => {
    function endTicketDrag() {
      setTicketDragActive(false);
      setDragOverId(null);
      if (!drawerOpen) setPeekSectionId(null);
    }
    window.addEventListener('dragend', endTicketDrag);
    return () => window.removeEventListener('dragend', endTicketDrag);
  }, [drawerOpen]);

  const showPanel = Boolean(
    panelSection &&
      !isTicketFooterFolder(panelSection) &&
      (panelSection.children.length > 0 ||
        panelSection.slug === 'inbox' ||
        creatingParentId === panelSection.id ||
        (creatingParentId === 'root' && panelSection.slug === 'inbox')),
  );

  // Panel is position:absolute over the ticket list, so aside mouseleave often
  // never fires when the pointer moves onto emails. Close whenever the pointer
  // is outside the rail + flyout (desktop hover mode only).
  useEffect(() => {
    if (!showPanel || drawerOpen) return;

    function clearLeaveTimer() {
      if (leaveTimerRef.current != null) {
        window.clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
    }

    function closePeek() {
      clearLeaveTimer();
      if (creatingParentIdRef.current || ticketDragActiveRef.current) return;
      setPeekSectionId(null);
    }

    function onPointerMove(e: PointerEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) {
        clearLeaveTimer();
        return;
      }
      if (leaveTimerRef.current != null) return;
      leaveTimerRef.current = window.setTimeout(closePeek, 80);
    }

    function onPointerDown(e: PointerEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      closePeek();
    }

    document.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      clearLeaveTimer();
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [showPanel, drawerOpen]);

  async function submitFolder() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const parentId = creatingParentId === 'root' ? null : creatingParentId;
      await onCreateFolder(name, parentId);
      setNewName('');
      setCreatingParentId(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(folder: TicketFolder) {
    const ok = window.confirm(
      `Delete folder “${folder.name}”? Tickets in this folder and its subfolders will move to Inbox.`,
    );
    if (!ok) return;
    await onDeleteFolder(folder.id);
  }

  function clearLeaveTimer() {
    if (leaveTimerRef.current != null) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }

  function handleRailEnter(node: TicketFolderNode) {
    clearLeaveTimer();
    if (isTicketFooterFolder(node)) {
      setPeekSectionId(null);
      return;
    }
    if (node.children.length > 0 || node.slug === 'inbox') {
      setPeekSectionId(node.id);
    } else {
      setPeekSectionId(null);
    }
  }

  function handleSelectFolder(folderId: string) {
    clearLeaveTimer();
    setPeekSectionId(null);
    onSelect(folderId);
  }

  function handleRailClick(node: TicketFolderNode) {
    handleSelectFolder(node.id);
    if (isTicketFooterFolder(node)) {
      setPeekSectionId(null);
      return;
    }
    // Keep flyout open after selecting a section that has subfolders (desktop hover).
    if (node.children.length > 0 || node.slug === 'inbox') {
      setPeekSectionId(node.id);
    } else {
      setPeekSectionId(null);
    }
  }

  function handleLayoutLeave(e: { relatedTarget: EventTarget | null; currentTarget: HTMLElement }) {
    if (creatingParentId || ticketDragActive || drawerOpen) return;
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    clearLeaveTimer();
    leaveTimerRef.current = window.setTimeout(() => {
      if (creatingParentIdRef.current || ticketDragActiveRef.current) return;
      setPeekSectionId(null);
    }, 80);
  }

  function handleLayoutEnter() {
    clearLeaveTimer();
  }

  function handleTicketDragOver(e: DragEvent, node: TicketFolderNode) {
    if (!onDropTicket) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setTicketDragActive(true);
    setDragOverId(node.id);
    handleRailEnter(node);
  }

  return (
    <aside
      ref={rootRef}
      className={`tickets-sidebar tickets-sidebar--dock${showPanel ? ' tickets-sidebar--panel-open' : ''}`}
      aria-label="Ticket folders"
      onMouseEnter={handleLayoutEnter}
      onMouseLeave={handleLayoutLeave}
      onDragEnter={(e) => {
        if (!onDropTicket) return;
        if (e.dataTransfer.types.includes('text/ticket-id')) {
          setTicketDragActive(true);
        }
      }}
    >
      <div className="tickets-sidebar__dock-head">
        <span className="tickets-sidebar__dock-folder-icon" aria-hidden>
          📁
        </span>
      </div>

      <div className="tickets-sidebar__dock-layout">
        <div className="tickets-sidebar__dock-rail">
          <div className="tickets-sidebar__dock-rail-main">
            {main.map((node) => {
              const unread = folderUnreadTotal(node, unreadByFolder);
              const active =
                node.id === activeFolderId ||
                node.children.some((child) => child.id === activeFolderId);
              const dragOver = dragOverId === node.id;
              return (
                <button
                  key={node.id}
                  type="button"
                  className={[
                    'tickets-sidebar__rail-btn',
                    active ? ' tickets-sidebar__rail-btn--active' : '',
                    peekSectionId === node.id ? ' tickets-sidebar__rail-btn--peek' : '',
                    dragOver ? ' tickets-sidebar__item--drag-over' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => handleRailEnter(node)}
                  onFocus={() => handleRailEnter(node)}
                  onClick={() => handleRailClick(node)}
                  onDragEnter={(e) => {
                    if (!onDropTicket) return;
                    handleTicketDragOver(e, node);
                  }}
                  onDragOver={(e) => handleTicketDragOver(e, node)}
                  onDragLeave={() => {
                    if (dragOverId === node.id) setDragOverId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setTicketDragActive(false);
                    setDragOverId(null);
                    const ticketId = e.dataTransfer.getData('text/ticket-id');
                    if (ticketId && onDropTicket) onDropTicket(ticketId, node.id);
                  }}
                >
                  <span className="tickets-sidebar__rail-label">{node.name}</span>
                  {unread > 0 ? (
                    <span className="tickets-sidebar__rail-badge">{unread}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {footer.length > 0 ? (
            <div className="tickets-sidebar__dock-rail-footer">
              {footer.map((node) => {
                const unread = unreadByFolder[node.id] ?? 0;
                const active = node.id === activeFolderId;
                const dragOver = dragOverId === node.id;
                return (
                  <button
                    key={node.id}
                    type="button"
                    className={[
                      'tickets-sidebar__rail-btn',
                      active ? ' tickets-sidebar__rail-btn--active' : '',
                      dragOver ? ' tickets-sidebar__item--drag-over' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setPeekSectionId(null)}
                    onClick={() => handleRailClick(node)}
                    onDragOver={(e) => {
                      if (!onDropTicket) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setTicketDragActive(true);
                      setPeekSectionId(null);
                      setDragOverId(node.id);
                    }}
                    onDragLeave={() => {
                      if (dragOverId === node.id) setDragOverId(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setTicketDragActive(false);
                      setDragOverId(null);
                      const ticketId = e.dataTransfer.getData('text/ticket-id');
                      if (ticketId && onDropTicket) onDropTicket(ticketId, node.id);
                    }}
                  >
                    <span className="tickets-sidebar__rail-label">{node.name}</span>
                    {unread > 0 ? (
                      <span className="tickets-sidebar__rail-badge">{unread}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {showPanel && panelSection ? (
          <div
            className="tickets-sidebar__dock-panel"
            onDragEnter={() => setTicketDragActive(true)}
          >
            <SubfolderPanel
              node={panelSection}
              unreadByFolder={unreadByFolder}
              activeFolderId={activeFolderId}
              dragOverId={dragOverId}
              creatingParentId={creatingParentId}
              newName={newName}
              busy={busy}
              onSelect={handleSelectFolder}
              onDropTicket={onDropTicket}
              onStartCreate={(parentId) => {
                setCreatingParentId(parentId);
                setNewName('');
                setPeekSectionId(parentId);
              }}
              onDelete={(folder) => void handleDelete(folder)}
              setDragOverId={setDragOverId}
              setNewName={setNewName}
              submitFolder={submitFolder}
              allowCreateFolders={allowCreateFolders}
            />
          </div>
        ) : null}
      </div>
    </aside>
  );
}
